// Premium-підписка: тарифи, переваги, статус активної підписки.
// Натискаючи "Активувати" — POST /premium/checkout повертає redirectUrl,
// який ми відкриваємо через Telegram.WebApp.openLink (зовнішній браузер).
// Браузер на тій сторінці одразу POST'ить форму на WayForPay.
// Після оплати webhook автоматично активує Premium.
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { haptic, openExternalLink } from '../lib/telegram';
import { IconTile, TabGlyph } from '../components/AppIcons';
import { isNativeApp } from '../lib/push';
import { NativePurchase } from '../components/NativePurchase';

export function Premium() {
  const { data, loading, error, reload } = useFetch(() => api.premium.get(), []);
  const [selectedId, setSelectedId] = useState<string>('3m');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  // Промокод
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<{
    code: string;
    discountPercent: number;
    originalPrice: number;
    discount: number;
    finalPrice: number;
    forPlan: string;
  } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);

  if (loading) return <Loader />;
  if (error) return <ErrorView error={error} onRetry={reload} />;
  if (!data) return null;

  // У нативному Android-додатку не можна проводити оплату цифрових товарів
  // повз Google Play Billing. Тож в апці ховаємо внутрішню оплату (WayForPay)
  // і перекидаємо людину оформити Premium на сайті (у зовнішньому браузері).
  const native = isNativeApp();

  const plan = data.plans.find((p) => p.id === selectedId) ?? data.plans[1] ?? data.plans[0]!;
  const validPromo = promo && promo.forPlan === plan.id ? promo : null;
  const finalPrice = validPromo ? validPromo.finalPrice : plan.priceUah;

  async function applyPromo() {
    const code = promoInput.trim().toUpperCase();
    if (!code || promoChecking) return;
    setPromoChecking(true);
    setPromoError(null);
    try {
      const res = await api.premium.checkPromocode(code, plan.id as '1m' | '3m' | '6m');
      setPromo({ ...res, forPlan: plan.id });
      haptic.success();
    } catch (e) {
      setPromoError(e instanceof ApiError ? e.message : 'Невалідний промокод');
      haptic.error();
    } finally {
      setPromoChecking(false);
    }
  }

  function clearPromo() {
    setPromo(null);
    setPromoInput('');
    setPromoError(null);
  }

  async function activate() {
    if (paying) return;
    if (!data!.paymentReady) {
      alert('Оплату ще не налаштовано. Звернись до адміністратора.');
      return;
    }
    haptic.medium();
    setPaying(true);
    setPayError(null);
    try {
      const res = await api.premium.checkout(
        plan.id as '1m' | '3m' | '6m',
        validPromo?.code,
      );
      // Відкриваємо нашу проміжну сторінку у системному браузері.
      // Вона одразу POST'ить форму на secure.wayforpay.com.
      openExternalLink(res.redirectUrl);
      // Кулдаун: юзер пішов на оплату — тримаємо кнопку неактивною кілька секунд,
      // щоб скажені повторні тапи не плодили checkout'и (бекенд теж дедупить).
      setTimeout(() => setPaying(false), 5000);
    } catch (e) {
      setPayError(
        e instanceof ApiError ? e.message : 'Не вдалось перейти до оплати. Спробуй ще раз.',
      );
      haptic.error();
      setPaying(false);
    }
  }

  return (
    <>
      <PageHeader title="Premium" subtitle="Розблокуй усі режими" />
      <div className="space-y-4 p-4">
        {data.isPremium && data.premiumUntil && (
          <section className="card border border-tg-success">
            <div className="flex items-center gap-3">
              <IconTile kind="crown" className="h-11 w-11" glyphClass="h-6 w-6" />
              <div>
                <div className="font-medium">Premium активний</div>
                <div className="text-xs text-tg-hint">
                  До {new Date(data.premiumUntil).toLocaleDateString('uk-UA')}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="card relative overflow-hidden bg-gradient-to-br from-yellow-500/10 via-tg-secondary-bg to-tg-secondary-bg">
          <div className="absolute -right-4 -top-4 text-yellow-500 opacity-10">
            <TabGlyph kind="crown" className="h-24 w-24" />
          </div>
          <div className="relative space-y-3">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-yellow-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-yellow-600">
              <TabGlyph kind="crown" className="h-3.5 w-3.5" />
              <span>Premium</span>
            </div>
            <h2 className="text-xl font-bold leading-snug">
              Склади іспит<br />з першої спроби
            </h2>
            <p className="text-sm text-tg-hint">
              Безкоштовно ти бачиш лише 40% питань. З Premium — повний доступ до всіх режимів і
              понад 2 300 запитань для впевненої підготовки.
            </p>
            <div className="grid grid-cols-2 gap-1.5 pt-1 text-xs">
              <Bullet icon="exam">Повна база питань (100%)</Bullet>
              <Bullet icon="topics">Вчи по темах</Bullet>
              <Bullet icon="tickets">Тренувальні білети</Bullet>
              <Bullet icon="hardest">100 найскладніших</Bullet>
              <Bullet icon="mistakes">Мої помилки</Bullet>
              <Bullet icon="bookmarks">Закладки</Bullet>
              {/* Битви — соц-фіча через Telegram; у нативній апці сховані */}
              {!native && <Bullet icon="battle">Битви з друзями</Bullet>}
            </div>
          </div>
        </section>

        {native ? (
          <NativePurchase
            appUserId={data.appUserId}
            publicKey={data.revenuecatPublicKey}
            plans={data.plans}
            onActivated={reload}
          />
        ) : (
          <>
        {data.specialPricing && (
          <div className="rounded-xl border border-tg-success bg-tg-success/10 p-3 text-center text-sm font-semibold text-tg-success">
            Спеціальна ціна за твоїм посиланням{data.specialLabel ? ` (${data.specialLabel})` : ''}
          </div>
        )}

        <section className="space-y-2">
          {data.plans.map((p) => {
            const active = p.id === selectedId;
            const top = p.id === '3m';
            return (
              <button
                key={p.id}
                onClick={() => {
                  haptic.selection();
                  setSelectedId(p.id);
                }}
                className={`card relative flex w-full items-center justify-between text-left transition ${
                  active ? 'border-2 border-tg-success' : 'border-2 border-transparent'
                }`}
              >
                {top && (
                  <span className="absolute -top-2 right-3 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold text-white">
                    Топ вибір
                  </span>
                )}
                <div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-tg-success">{p.priceUah} ₴</div>
                    {p.overridden && p.basePriceUah && p.basePriceUah > p.priceUah && (
                      <div className="text-base font-normal text-tg-hint line-through">
                        {p.basePriceUah} ₴
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-tg-hint">
                    {p.months} {p.months === 1 ? 'місяць' : p.months < 5 ? 'місяці' : 'місяців'}
                    {p.months > 1 && ` · ${Math.round(p.priceUah / p.months)} ₴/міс`}
                  </div>
                </div>
                <div className="text-tg-hint">{active ? '✓' : '›'}</div>
              </button>
            );
          })}
        </section>

        {/* Промокод */}
        <section className="card space-y-2">
          {!validPromo ? (
            <>
              <label className="block text-xs font-medium text-tg-hint">Маєш промокод?</label>
              <div className="flex gap-2">
                <input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Введи код"
                  className="flex-1 rounded-lg bg-tg-bg px-3 py-2 font-mono text-sm uppercase tracking-wider"
                />
                <button
                  onClick={applyPromo}
                  disabled={!promoInput.trim() || promoChecking}
                  className="rounded-lg bg-tg-button px-4 text-sm font-semibold text-tg-button-text disabled:opacity-50"
                >
                  {promoChecking ? '…' : 'Застосувати'}
                </button>
              </div>
              {promoError && <div className="text-xs text-tg-destructive">{promoError}</div>}
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <code className="rounded bg-tg-success/15 px-2 py-0.5 font-mono font-bold text-tg-success">
                    {validPromo.code}
                  </code>
                  <span className="text-xs text-tg-success">−{validPromo.discountPercent}%</span>
                </div>
                <div className="mt-1 text-xs text-tg-hint">
                  Економія: {validPromo.discount} ₴ ·{' '}
                  <span className="line-through">{validPromo.originalPrice} ₴</span>{' '}
                  → <strong className="text-tg-text">{validPromo.finalPrice} ₴</strong>
                </div>
              </div>
              <button
                onClick={clearPromo}
                className="text-xs text-tg-hint underline-offset-4 hover:underline"
              >
                Скасувати
              </button>
            </div>
          )}
        </section>

        {payError && (
          <div className="card border border-tg-destructive text-sm text-tg-destructive">{payError}</div>
        )}

        <button
          onClick={activate}
          disabled={paying || !data.paymentReady}
          className="btn-primary w-full bg-tg-success text-white disabled:opacity-50"
        >
          {!data.paymentReady
            ? 'Оплату ще не налаштовано'
            : paying
              ? 'Підготовка оплати…'
              : `Активувати · ${finalPrice} ₴`}
        </button>
        <p className="text-center text-[11px] text-tg-hint">
          Безпечна оплата через WayForPay. Premium активується автоматично після оплати.
        </p>
          </>
        )}

        <section className="card space-y-3 p-0 overflow-hidden">
          <div className="bg-tg-bg/40 px-4 py-3">
            <h3 className="flex items-center gap-1.5 text-base font-semibold">
              Що відкриває
              <span className="inline-flex items-center gap-1 text-yellow-500">
                <TabGlyph kind="crown" className="h-4 w-4" /> Premium
              </span>
            </h3>
            <p className="mt-0.5 text-xs text-tg-hint">
              Порівняння можливостей для безкоштовної та преміум версії
            </p>
          </div>

          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 px-4 pb-1 text-[10px] font-semibold uppercase tracking-wide text-tg-hint">
            <span>Можливість</span>
            <span className="w-12 text-center">Free</span>
            <span className="w-16 text-center text-yellow-600">Premium</span>
          </div>

          <div className="px-4 pb-4">
            <CompareRow
              icon="exam"
              title="Іспит як у СЦ МВС"
              desc="20 запитань, 20 хвилин, можна 2 помилки"
              free
              prem
            />
            <CompareRow
              icon="target"
              title="Повна база питань"
              desc="Безкоштовно — лише 40% бази; з Premium — усі 2300+ запитань"
              free={false}
              prem
            />
            <CompareRow
              icon="topics"
              title="Тренуйся за темами"
              desc="63 теми ПДР: знаки, перехрестя, обгін, гальмування…"
              free={false}
              prem
            />
            <CompareRow
              icon="tickets"
              title="Тренувальні білети"
              desc="20 випадкових запитань без обмеження часу"
              free={false}
              prem
            />
            <CompareRow
              icon="hardest"
              title="100 найскладніших"
              desc="Запитання, на яких найчастіше помиляються"
              free={false}
              prem
            />
            <CompareRow
              icon="mistakes"
              title="Робота над помилками"
              desc="Автоматично збираються — повторюй до закріплення"
              free={false}
              prem
            />
            <CompareRow
              icon="bookmarks"
              title="Закладки"
              desc="Збережи складні запитання — повертайся будь-коли"
              free={false}
              prem
            />
            <CompareRow
              icon="battle"
              title="Битви з друзями"
              desc="Дуелі 1v1 на 5–20 запитаннях, рейтинг"
              free={false}
              prem
            />
            <CompareRow
              icon="noads"
              title="Без реклами"
              desc="Жодних відволікань — тільки навчання"
              free={false}
              prem
              last
            />
          </div>
        </section>

        <p className="text-center text-xs text-tg-hint">
          Активуй усі можливості, щоб скласти іспит з 1-го разу.
        </p>
      </div>
    </>
  );
}

function Bullet({
  icon,
  children,
}: {
  icon: Parameters<typeof IconTile>[0]['kind'];
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-lg bg-tg-bg/40 px-2 py-1.5">
      <IconTile kind={icon} className="h-5 w-5 rounded-md" glyphClass="h-3 w-3" />
      <span className="truncate">{children}</span>
    </div>
  );
}

function CompareRow({
  icon,
  title,
  desc,
  free,
  prem,
  last,
}: {
  icon: Parameters<typeof IconTile>[0]['kind'];
  title: string;
  desc: string;
  free: boolean;
  prem: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[1fr_auto_auto] items-center gap-x-3 py-3 ${
        last ? '' : 'border-b border-tg-bg/40'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <IconTile kind={icon} className="mt-0.5 h-7 w-7 rounded-lg" glyphClass="h-4 w-4" />
        <div className="min-w-0">
          <div className="text-sm font-medium leading-tight">{title}</div>
          <div className="mt-0.5 text-xs text-tg-hint">{desc}</div>
        </div>
      </div>
      <span className="w-12 text-center">
        {free ? (
          <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-tg-success/20 text-sm text-tg-success">
            ✓
          </span>
        ) : (
          <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-tg-destructive/15 text-sm text-tg-destructive">
            ✕
          </span>
        )}
      </span>
      <span className="w-16 text-center">
        {prem ? (
          <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-yellow-500/20 text-sm text-yellow-600">
            ✓
          </span>
        ) : (
          <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-tg-destructive/15 text-sm text-tg-destructive">
            ✕
          </span>
        )}
      </span>
    </div>
  );
}
